import { Trans } from "react-i18next";

import { ReactComponent as advancedSVG } from "@/assets/icons/fitness-level/advanced.svg";
import { ReactComponent as beginnerSVG } from "@/assets/icons/fitness-level/beginner.svg";
import { ReactComponent as intermediateSVG } from "@/assets/icons/fitness-level/intermediate.svg";
import { ReactComponent as achievementsSVG } from "@/assets/icons/motivation/achievements.svg";
import { ReactComponent as challengesSVG } from "@/assets/icons/motivation/challenges.svg";
import { ReactComponent as challengingSVG } from "@/assets/icons/motivation/challenging.svg";
import { ReactComponent as disciplineSVG } from "@/assets/icons/motivation/discipline.svg";
import { ReactComponent as enjoyableSVG } from "@/assets/icons/motivation/enjoyable.svg";
import { ReactComponent as fickleSVG } from "@/assets/icons/motivation/fickle.svg";
import { ReactComponent as inconvenientSVG } from "@/assets/icons/motivation/inconvenient.svg";
import { ReactComponent as musicSVG } from "@/assets/icons/motivation/music.svg";
import { ReactComponent as necessarySVG } from "@/assets/icons/motivation/necessary.svg";
import { ReactComponent as progressSVG } from "@/assets/icons/motivation/progress-tracking.svg";
import { ReactComponent as resilientSVG } from "@/assets/icons/motivation/resilient.svg";
import { ReactComponent as steadfastSVG } from "@/assets/icons/motivation/steadfast.svg";
import { ActivityLevels } from "@/constants/activityLevel";
import {
    BodyInjures,
    FEMALE_BODY_INJURES_IMG,
    FEMALE_BODY_INJURES_IMG_SELECTED,
    MALE_BODY_INJURES_IMG,
    MALE_BODY_INJURES_IMG_SELECTED,
} from "@/constants/body-injures";
import {
    BodyMetricTypes,
    FemaleBodyFatRangeTypes,
    FemaleImagesByBodyFatRange,
    ImagesByBodyFatRange,
    MaleBodyFatRangeTypes,
} from "@/constants/bodyMetrics";
import { FitnessGoals, seniorsGoalIngTextById, seniorsGoalTextById } from "@/constants/fitnessGoal";
import { FitnessLevels, getFitnessLevelLabel } from "@/constants/fitnessLevels";
import { questionGroups } from "@/constants/questionGroups";
import { QUESTIONS_IDS, QuestionTypes } from "@/constants/questionsIds";
import { Equipment, GymType, TrainingEnvironment } from "@/constants/trainingEnvironment";
import femaleBodyArms from "@/female/assets/body-injures/arms.webp";
import femaleBodyBack from "@/female/assets/body-injures/back.webp";
import femaleBodyElbows from "@/female/assets/body-injures/elbows.webp";
import femaleBodyFeet from "@/female/assets/body-injures/feet.webp";
import femaleBodyKnees from "@/female/assets/body-injures/knees.webp";
import femaleBodyLegs from "@/female/assets/body-injures/legs.webp";
import femaleBodyNeck from "@/female/assets/body-injures/neck.webp";
import femaleBodyNoIssues from "@/female/assets/body-injures/no-issues.webp";
import femaleBodyShoulders from "@/female/assets/body-injures/shoulders.webp";
import femaleMuscleGain from "@/female/assets/fitness-goal/muscle-gain.webp";
import femaleOverallFitness from "@/female/assets/fitness-goal/weight-loss.webp";
import femaleGym from "@/female/assets/location/gym.webp";
import femaleHome from "@/female/assets/location/home.webp";
import femaleMixed from "@/female/assets/location/mix.webp";
import i18n from "@/localization/i18next";
import bodyArms from "@/male/assets/body-injures/arms.webp";
import bodyBack from "@/male/assets/body-injures/back.webp";
import bodyElbows from "@/male/assets/body-injures/elbows.webp";
import bodyFeet from "@/male/assets/body-injures/feet.webp";
import bodyKnees from "@/male/assets/body-injures/knees.webp";
import bodyLegs from "@/male/assets/body-injures/legs.webp";
import bodyNeck from "@/male/assets/body-injures/neck.webp";
import bodyNoIssues from "@/male/assets/body-injures/no-issues.webp";
import bodyShoulders from "@/male/assets/body-injures/shoulders.webp";
import muscleGain from "@/male/assets/goals/muscle-gain.webp";
import overallFitness from "@/male/assets/goals/weight-loss.webp";
import gym from "@/male/assets/location/gym.webp";
import home from "@/male/assets/location/home.webp";
import mixed from "@/male/assets/location/mixed.webp";
import femaleWeightLoss from "@/seniors/assets/goals/weight-loss-female.webp";
import weightLoss from "@/seniors/assets/goals/weight-loss-male.webp";
import {
    DesiredWorkoutTimeAnswers,
    DietTypeAnswers,
    FitnessExperienceAnswers,
    HealthProblemsAnswers,
    ImportantElementsAnswers,
    SleepAmountAnswers,
    SportPreferAnswers,
    Train3MonthsAnswers,
    YesNoAnswers,
} from "@/types/answers";
import { Genders } from "@/types/gender";
import { Answers } from "@/utils/AnswersProvider";

import bands from "../../assets/img/questions/equipment/bands.webp";
import brbll from "../../assets/img/questions/equipment/brbll.webp";
import dumbbell from "../../assets/img/questions/equipment/dumbbell.webp";
import kettleber from "../../assets/img/questions/equipment/kettleber.webp";
import mat from "../../assets/img/questions/equipment/mat.webp";
import none from "../../assets/img/questions/equipment/none.webp";
import pub from "../../assets/img/questions/equipment/pub.webp";
import rope from "../../assets/img/questions/equipment/rope.webp";

export const getQuestions = (prefix = "man", answers: Answers, isFemale: boolean) => [
    {
        id: QUESTIONS_IDS.ASPECTS_MOST_CONCERN,
        event: `${prefix}_${QUESTIONS_IDS.ASPECTS_MOST_CONCERN}`,
        type: QuestionTypes.Checkbox,
        title: "What aspects of your current fitness concern you the most?",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "look_and_feel",
                text: "How I look and feel in clothes",
            },
            {
                id: "strength_and_energy_lack",
                text: "Lack of strength and energy",
            },
            {
                id: "workout_routine",
                text: "Inconsistent workout routine",
            },
            {
                id: "pain",
                text: "Ongoing pain & fatigue",
            },
            {
                id: "progress_despite_efforts",
                text: "No progress despite efforts",
            },
            {
                id: "weight_gain_despite_workouts",
                text: "Weight gain despite workouts",
            },
            {
                id: "fear_of_falling",
                text: "Poor balance and fear of falling",
            },
            {
                id: "other",
                text: "Other",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PRIMARY_GOAL,
        event: `${prefix}_${QUESTIONS_IDS.PRIMARY_GOAL}`,
        type: QuestionTypes.RadioImageLeft,
        title: "Which transformation would make the biggest impact on your life?",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: FitnessGoals.LoseWeight,
                text: seniorsGoalTextById[FitnessGoals.LoseWeight],
                img: isFemale ? femaleWeightLoss : weightLoss,
            },
            {
                id: FitnessGoals.GainMuscle,
                text: seniorsGoalTextById[FitnessGoals.GainMuscle],
                img: isFemale ? femaleMuscleGain : muscleGain,
            },
            {
                id: FitnessGoals.GetActive,
                text: seniorsGoalTextById[FitnessGoals.GetActive],
                img: isFemale ? femaleOverallFitness : overallFitness,
            },
        ],
    },
    ...((answers[QUESTIONS_IDS.ASPECTS_MOST_CONCERN] as string[])?.includes("look_and_feel")
        ? [
              {
                  id: QUESTIONS_IDS.FEEL_IN_YOUR_CLOTHES,
                  event: `${prefix}_${QUESTIONS_IDS.FEEL_IN_YOUR_CLOTHES}`,
                  type: QuestionTypes.Checkbox,
                  title: "How would you like to feel in your clothes?",
                  groupTitle: questionGroups.Goals,
                  answers: [
                      {
                          id: "more_toned",
                          text: "More toned and fit-looking",
                      },
                      {
                          id: "stronger",
                          text: "Stronger and more stable",
                      },
                      {
                          id: "slimmer",
                          text: "Slimmer and more energetic",
                      },
                      {
                          id: "more_confident",
                          text: "More confident in my current clothes",
                      },
                      {
                          id: "comfortable",
                          text: "Comfortable moving without restrictions",
                      },
                  ],
              },
          ]
        : []),
    {
        id: QUESTIONS_IDS.PROTECT_AGAINST_AGING,
        event: `${prefix}_${QUESTIONS_IDS.PROTECT_AGAINST_AGING}`,
        type: QuestionTypes.Radio,
        title: i18n.t("protect_against_aging_prompt"),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: YesNoAnswers.Yes,
                text: i18n.t("yes"),
            },
            {
                id: YesNoAnswers.No,
                text: i18n.t("no"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.STATEMENT_PROGRAM_UNDERSTANDS_MY_LIMITATIONS,
        type: QuestionTypes.Rate,
        event: `${prefix}_${QUESTIONS_IDS.STATEMENT_PROGRAM_UNDERSTANDS_MY_LIMITATIONS}`,
        title: i18n.t("rate_prompt"),
        groupTitle: questionGroups.Goals,
        subtitle:
            "I want a workout program that understands my limitations but still helps me progress.",
        maxRate: 5,
    },
    {
        id: QUESTIONS_IDS.STATEMENT_SAFE_GUIDANCE,
        type: QuestionTypes.Rate,
        event: `${prefix}_${QUESTIONS_IDS.STATEMENT_SAFE_GUIDANCE}`,
        title: i18n.t("rate_prompt"),
        groupTitle: questionGroups.Goals,
        subtitle:
            "I'd feel more confident exercising with guidance that keeps me safe and accountable.",
        maxRate: 5,
    },
    {
        id: QUESTIONS_IDS.CONDITIONS_AFFECT_DAILY_ACTIVITIES,
        type: QuestionTypes.Checkbox,
        event: `${prefix}_${QUESTIONS_IDS.CONDITIONS_AFFECT_DAILY_ACTIVITIES}`,
        title: "Do any of these conditions affect your daily activities?",
        groupTitle: questionGroups.HealthCondition,
        answers: [
            {
                id: "arthritis",
                text: "Arthritis",
            },
            {
                id: "osteoporosis",
                text: "Osteoporosis",
            },
            {
                id: "high_blood_pressure",
                text: "High blood pressure",
            },
            {
                id: "heart_conditions",
                text: "Heart conditions",
            },
            {
                id: "balance_issues",
                text: "Balance issues",
            },
            {
                id: "none",
                text: "None of these",
            },
            {
                id: "prefer_not_to_answer",
                text: "Prefer not to answer",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.HEALTH_PROBLEMS,
        event: `${prefix}_${QUESTIONS_IDS.HEALTH_PROBLEMS}`,
        type: QuestionTypes.CheckboxImageLeft,
        title: i18n.t("injuries_prompt"),
        subtitle: i18n.t("injuries_motivation_prompt"),
        groupTitle: questionGroups.HealthCondition,
        emptyAnswer: {
            id: HealthProblemsAnswers.None,
            text: i18n.t("no_issues"),
            img: isFemale ? femaleBodyNoIssues : bodyNoIssues,
        },
        answers: [
            {
                id: HealthProblemsAnswers.BackPain,
                text: i18n.t("back"),
                img: isFemale ? femaleBodyBack : bodyBack,
            },
            {
                id: HealthProblemsAnswers.KneePain,
                text: i18n.t("knees"),
                img: isFemale ? femaleBodyKnees : bodyKnees,
            },
            {
                id: HealthProblemsAnswers.ShoulderPain,
                text: i18n.t("shoulders"),
                img: isFemale ? femaleBodyShoulders : bodyShoulders,
            },
            {
                id: HealthProblemsAnswers.NeckPain,
                text: i18n.t("neck"),
                img: isFemale ? femaleBodyNeck : bodyNeck,
            },
            {
                id: HealthProblemsAnswers.ArmPain,
                text: i18n.t("arms"),
                img: isFemale ? femaleBodyArms : bodyArms,
            },
            {
                id: HealthProblemsAnswers.ElbowPain,
                text: i18n.t("elbows"),
                img: isFemale ? femaleBodyElbows : bodyElbows,
            },
            {
                id: HealthProblemsAnswers.LegPain,
                text: i18n.t("legs"),
                img: isFemale ? femaleBodyLegs : bodyLegs,
            },
            {
                id: HealthProblemsAnswers.FootPain,
                text: i18n.t("feet"),
                img: isFemale ? femaleBodyFeet : bodyFeet,
            },
        ],
    },
    ...(isFemale
        ? [
              {
                  id: QUESTIONS_IDS.GOING_THROUNG_MENOPAUSE,
                  type: QuestionTypes.Radio,
                  event: `${prefix}_${QUESTIONS_IDS.GOING_THROUNG_MENOPAUSE}`,
                  title: "Are you currently going through menopause?",
                  groupTitle: questionGroups.HealthCondition,
                  answers: [
                      {
                          id: "perimenopause",
                          text: "I'm in perimenopause",
                      },
                      {
                          id: "menopause",
                          text: "I'm in menopause",
                      },
                      {
                          id: "postmenopause",
                          text: "I'm in postmenopause",
                      },
                      {
                          id: "none",
                          text: "None of the above",
                      },
                  ],
              },
              ...((answers[QUESTIONS_IDS.GOING_THROUNG_MENOPAUSE] as string) === "menopause"
                  ? [
                        {
                            id: QUESTIONS_IDS.MENOPAUSE_SYMPTOMS,
                            type: QuestionTypes.Checkbox,
                            event: `${prefix}_${QUESTIONS_IDS.MENOPAUSE_SYMPTOMS}`,
                            title: "Which menopause symptoms are you experiencing?",
                            groupTitle: questionGroups.HealthCondition,
                            answers: [
                                {
                                    id: "hot_flashes",
                                    text: "Hot flashes",
                                },
                                {
                                    id: "night_sweats",
                                    text: "Night sweats",
                                },
                                {
                                    id: "fatigue",
                                    text: "Fatigue",
                                },
                                {
                                    id: "sleep_problems",
                                    text: "Sleep problems",
                                },
                                {
                                    id: "weight_gain",
                                    text: "Weight gain",
                                },
                                {
                                    id: "mood_changes",
                                    text: "Mood changes",
                                },
                                {
                                    id: "anxiety",
                                    text: "Anxiety",
                                },
                                {
                                    id: "joint_stiffness",
                                    text: "Joint stiffness",
                                },
                                {
                                    id: "none",
                                    text: "None of these",
                                },
                            ],
                        },
                    ]
                  : []),
          ]
        : []),
    {
        id: QUESTIONS_IDS.WORKED_WITH_HEALTH_PROFESSIONALS,
        type: QuestionTypes.Checkbox,
        event: `${prefix}_${QUESTIONS_IDS.WORKED_WITH_HEALTH_PROFESSIONALS}`,
        title: "Have you worked with any health professionals on your fitness?",
        groupTitle: questionGroups.HealthCondition,
        answers: [
            {
                id: "physical_therapist",
                text: "Physical therapist",
            },
            {
                id: "personal_trainer",
                text: "Personal trainer",
            },
            {
                id: "medical_doctor",
                text: "Medical doctor",
            },
            {
                id: "nutritionist",
                text: "Nutritionist",
            },
            {
                id: "none_yet",
                text: "None yet, but interested",
            },
            {
                id: "not_planning",
                text: "No, and not planning to",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CONCERNS_YOU_MOST,
        type: QuestionTypes.Checkbox,
        event: `${prefix}_${QUESTIONS_IDS.CONCERNS_YOU_MOST}`,
        title: "What concerns you most about exercising?",
        groupTitle: questionGroups.HealthCondition,
        progressBarBreakPoint: true,
        answers: [
            {
                id: "proper_technique",
                text: "Proper form and technique",
            },
            {
                id: "risk_of_injury",
                text: "Risk of injury",
            },
            {
                id: "health_conditions",
                text: "Existing health conditions",
            },
            {
                id: "not_knowing_my_limits",
                text: "Not knowing my limits",
            },
            {
                id: "intensity_level",
                text: "Exercise intensity level",
            },
            {
                id: "none",
                text: "None of these concerns",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.FITNESS_EXPERIENCE,
        event: `${prefix}_${QUESTIONS_IDS.FITNESS_EXPERIENCE}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("fitness_experience_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: FitnessExperienceAnswers.PersonalTrainers,
                text: i18n.t("personal_trainers"),
            },
            {
                id: FitnessExperienceAnswers.FitnessApps,
                text: i18n.t("fitness_apps"),
            },
            {
                id: FitnessExperienceAnswers.WorkoutVideos,
                text: i18n.t("workout_videos"),
            },
            {
                id: FitnessExperienceAnswers.SelfTaught,
                text: i18n.t("self_taught"),
            },
            {
                id: FitnessExperienceAnswers.None,
                text: i18n.t("none"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.TRIED_TO_IMPROVE_YOUR_FITNESS,
        event: `${prefix}_${QUESTIONS_IDS.TRIED_TO_IMPROVE_YOUR_FITNESS}`,
        type: QuestionTypes.Checkbox,
        title: "What have you tried to improve your fitness?",
        subtitle:
            "Zing adapts your plan based on the activities and behaviors you’ve adopted in the past.",
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: "nutrition_changes",
                text: "Nutrition changes",
                description: "Restrictive diets, fasting, meal plans",
            },
            {
                id: "weight_training",
                text: "Weight training",
                description: "Powerlifting, bodybuilding, circuit training",
            },
            {
                id: "cardio",
                text: "Cardio",
                description: "Running, rowing, cycling",
            },
            {
                id: "bodyweight_training",
                text: "Bodyweight training",
                description: "Yoga, calisthenics, pilates",
            },
            {
                id: "group_sports",
                text: "Group sports",
                description: "Football, tennis, basketball",
            },
            {
                id: "martial_arts",
                text: "Martial arts",
                description: "Boxing, MMA, kung fu",
            },
            {
                id: "other",
                text: "Other",
                description: "I’m new to fitness",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.FITNESS_LEVEL,
        event: `${prefix}_${QUESTIONS_IDS.FITNESS_LEVEL}`,
        type: QuestionTypes.Radio,
        title: i18n.t("fitness_level_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: FitnessLevels.Beginner,
                text: i18n.t(FitnessLevels.Beginner),
                description: i18n.t("beginner_description", {
                    context: isFemale ? Genders.Female : Genders.Male,
                    defaultValue: "beginner_description",
                }),
                ...(isFemale ? {} : { svg: beginnerSVG }),
            },
            {
                id: FitnessLevels.Intermediate,
                text: getFitnessLevelLabel(
                    FitnessLevels.Intermediate,
                    isFemale ? Genders.Female : Genders.Male
                ),
                description: i18n.t("intermediate_description"),
                ...(isFemale ? {} : { svg: intermediateSVG }),
            },
            {
                id: FitnessLevels.Advanced,
                text: getFitnessLevelLabel(
                    FitnessLevels.Advanced,
                    isFemale ? Genders.Female : Genders.Male
                ),
                description: i18n.t("advanced_description"),
                ...(isFemale ? {} : { svg: advancedSVG }),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.TRAIN_3_MONTHS,
        event: `${prefix}_${QUESTIONS_IDS.TRAIN_3_MONTHS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("train_3months_prompt"),
        subtitle: i18n.t("train_3months_motivation_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: Train3MonthsAnswers.HardlyEver,
                text: i18n.t("hardly_ever"),
            },
            {
                id: Train3MonthsAnswers.One2PerWeek,
                text: i18n.t("1_2_per_week"),
            },
            {
                id: Train3MonthsAnswers.Three4PerWeek,
                text: i18n.t("3_4_per_week"),
            },
            {
                id: Train3MonthsAnswers.Five6PerWeek,
                text: i18n.t("5_6_per_week"),
            },
            {
                id: Train3MonthsAnswers.Everyday,
                text: i18n.t("everyday"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.FITNESS_PLANS_DIFFICULT_TO_FOLLOW,
        event: `${prefix}_${QUESTIONS_IDS.FITNESS_PLANS_DIFFICULT_TO_FOLLOW}`,
        type: QuestionTypes.Radio,
        title: "Were your previous fitness plans difficult to follow?",
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: YesNoAnswers.Yes,
                text: i18n.t("yes"),
            },
            {
                id: YesNoAnswers.No,
                text: i18n.t("no"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SUCCESS_MEASURE,
        event: `${prefix}_${QUESTIONS_IDS.SUCCESS_MEASURE}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("success_measure_prompt"),
        subtitle: i18n.t("success_measure_motivation_prompt"),
        groupTitle: questionGroups.TrainingPreferences,
        maxChosenOptions: 3,
        answers: [
            {
                id: "daily_function",
                text: "Daily function",
                description: "Easier movement in everyday tasks and activities",
            },
            {
                id: "functional_strength",
                text: "Functional strength",
                description: "Ability to lift, carry and move with less effort",
            },
            {
                id: "energy_levels",
                text: "Energy levels",
                description: "More vitality throughout the day without fatigue",
            },
            {
                id: "pain_reduction",
                text: "Pain reduction",
                description: "Less discomfort and stiffness during daily activities",
            },
            {
                id: "balance_stability",
                text: "Balance stability",
                description: "Confident movement with reduced risk of falling",
            },
            {
                id: "overall_mobility",
                text: "Overall mobility",
                description: "Better range of motion and freedom of movement",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.TARGET_AREAS,
        event: `${prefix}_${QUESTIONS_IDS.TARGET_AREAS}`,
        type: QuestionTypes.CheckboxInline,
        title: i18n.t("focus_areas_prompt"),
        groupTitle: questionGroups.TrainingPreferences,
        answers: [
            {
                id: BodyInjures.FullBody,
                text: i18n.t("full_body"),
                img: isFemale
                    ? FEMALE_BODY_INJURES_IMG[BodyInjures.FullBody]
                    : MALE_BODY_INJURES_IMG[BodyInjures.FullBody],
                selectedImg: isFemale
                    ? FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.FullBody]
                    : MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.FullBody],
            },
            {
                id: BodyInjures.Shoulders,
                text: i18n.t("shoulders"),
                img: isFemale
                    ? FEMALE_BODY_INJURES_IMG[BodyInjures.Shoulders]
                    : MALE_BODY_INJURES_IMG[BodyInjures.Shoulders],
                selectedImg: isFemale
                    ? FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Shoulders]
                    : MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Shoulders],
            },
            {
                id: BodyInjures.Arms,
                text: i18n.t("arms"),
                img: isFemale
                    ? FEMALE_BODY_INJURES_IMG[BodyInjures.Arms]
                    : MALE_BODY_INJURES_IMG[BodyInjures.Arms],
                selectedImg: isFemale
                    ? FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Arms]
                    : MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Arms],
            },
            {
                id: BodyInjures.Core,
                text: i18n.t("back"),
                img: isFemale
                    ? FEMALE_BODY_INJURES_IMG[BodyInjures.Core]
                    : MALE_BODY_INJURES_IMG[BodyInjures.Core],
                selectedImg: isFemale
                    ? FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Core]
                    : MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Core],
            },
            {
                id: BodyInjures.Chest,
                text: i18n.t("chest"),
                img: isFemale
                    ? FEMALE_BODY_INJURES_IMG[BodyInjures.Chest]
                    : MALE_BODY_INJURES_IMG[BodyInjures.Chest],
                selectedImg: isFemale
                    ? FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Chest]
                    : MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Chest],
            },
            {
                id: BodyInjures.Abs,
                text: i18n.t("belly"),
                img: isFemale
                    ? FEMALE_BODY_INJURES_IMG[BodyInjures.Abs]
                    : MALE_BODY_INJURES_IMG[BodyInjures.Abs],
                selectedImg: isFemale
                    ? FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Abs]
                    : MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Abs],
            },
            {
                id: BodyInjures.Glutes,
                text: i18n.t("butt"),
                img: isFemale
                    ? FEMALE_BODY_INJURES_IMG[BodyInjures.Glutes]
                    : MALE_BODY_INJURES_IMG[BodyInjures.Glutes],
                selectedImg: isFemale
                    ? FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Glutes]
                    : MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Glutes],
            },
            {
                id: BodyInjures.Legs,
                text: i18n.t("legs"),
                img: isFemale
                    ? FEMALE_BODY_INJURES_IMG[BodyInjures.Legs]
                    : MALE_BODY_INJURES_IMG[BodyInjures.Legs],
                selectedImg: isFemale
                    ? FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Legs]
                    : MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Legs],
            },
        ],
    },
    {
        id: QUESTIONS_IDS.WORKOUT_INTENSITY,
        event: `${prefix}_${QUESTIONS_IDS.WORKOUT_INTENSITY}`,
        type: QuestionTypes.Radio,
        title: "What workout intensity would you be most comfortable with?",
        groupTitle: questionGroups.TrainingPreferences,
        answers: [
            {
                id: "basic",
                text: "Basic",
            },
            {
                id: "moderate",
                text: "Moderate",
            },
            {
                id: "high",
                text: "High",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.LOCATION,
        event: `${prefix}_${QUESTIONS_IDS.LOCATION}`,
        type: QuestionTypes.RadioImageLeft,
        title: i18n.t("choose_location"),
        groupTitle: questionGroups.TrainingPreferences,
        answers: [
            {
                id: TrainingEnvironment.Home,
                text: i18n.t("home"),
                description: i18n.t("home_description"),
                img: isFemale ? femaleHome : home,
            },
            {
                id: TrainingEnvironment.Gym,
                text: i18n.t("gym"),
                description: i18n.t("gym_description"),
                img: isFemale ? femaleGym : gym,
            },
            {
                id: TrainingEnvironment.Mix,
                text: i18n.t("mix"),
                description: i18n.t("mixed_description"),
                img: isFemale ? femaleMixed : mixed,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.GYM_TYPE,
        event: `${prefix}_${QUESTIONS_IDS.GYM_TYPE}`,
        type: QuestionTypes.Radio,
        title: i18n.t("gym_type_prompt"),
        subtitle: i18n.t("equipment_suggestion"),
        groupTitle: questionGroups.TrainingPreferences,
        answers: [
            {
                id: GymType.Large,
                text: i18n.t("large_gym"),
                description: i18n.t("large_gym_types"),
            },
            {
                id: GymType.Small,
                text: i18n.t("small_gym"),
                description: i18n.t("small_gym_types"),
            },
            {
                id: GymType.Garage,
                text: i18n.t("garage_gym"),
                description: i18n.t("garage_gym_types"),
            },
            {
                id: GymType.Home,
                text: i18n.t("home_gym"),
                description: i18n.t("home_gym_types"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.EQUIPMENT_BY_ENVIRONMENT,
        event: `${prefix}_${QUESTIONS_IDS.EQUIPMENT_BY_ENVIRONMENT}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("home_equipment_prompt"),
        groupTitle: questionGroups.TrainingPreferences,
        answers: [
            {
                id: Equipment.Dumbbells,
                text: i18n.t("dumbbells"),
                img: dumbbell,
            },
            {
                id: Equipment.YogaMat,
                text: i18n.t("yoga_mat"),
                img: mat,
            },
            {
                id: Equipment.Barbell,
                text: i18n.t("barbell"),
                img: brbll,
            },
            {
                id: Equipment.JumpRope,
                text: i18n.t("jump_rope"),
                img: rope,
            },
            {
                id: Equipment.PullUps,
                text: i18n.t("pull_up_bar"),
                img: pub,
            },
            {
                id: Equipment.PowerBand,
                text: i18n.t("power_band"),
                img: bands,
            },
            {
                id: Equipment.Kettlebell,
                text: i18n.t("kettlebells"),
                img: kettleber,
            },
            {
                id: Equipment.No,
                text: i18n.t("none_of_the_above"),
                img: none,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.IMPORTANT_ELEMENTS,
        event: `${prefix}_${QUESTIONS_IDS.IMPORTANT_ELEMENTS}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("important_elements_prompt"),
        subtitle: i18n.t("important_elements_motivation_prompt"),
        groupTitle: questionGroups.TrainingPreferences,
        answers: [
            {
                id: ImportantElementsAnswers.Consistency,
                text: i18n.t("consistency"),
                description: i18n.t("consistency_description"),
            },
            {
                id: ImportantElementsAnswers.Commitment,
                text: i18n.t("commitment"),
                description: i18n.t("commitment_description"),
            },
            {
                id: ImportantElementsAnswers.Guidance,
                text: i18n.t("guidance"),
                description: i18n.t("guidance_description"),
            },
            {
                id: ImportantElementsAnswers.ExerciseVariety,
                text: i18n.t("exercise_variety"),
                description: i18n.t("exercise_variety_description"),
            },
            {
                id: ImportantElementsAnswers.LifestyleChanges,
                text: i18n.t("lifestyle_changes"),
                description: i18n.t("lifestyle_changes_description"),
            },
            {
                id: ImportantElementsAnswers.GoalFocus,
                text: i18n.t("goal_focus"),
                description: i18n.t("goal_focus_description", {
                    goal: seniorsGoalIngTextById[
                        answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals
                    ],
                }),
            },
            {
                id: ImportantElementsAnswers.Convenience,
                text: i18n.t("convenience"),
                description: i18n.t("convenience_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SPORT_PREFER,
        event: `${prefix}_${QUESTIONS_IDS.SPORT_PREFER}`,
        type: QuestionTypes.Radio,
        title: i18n.t("workout_preference_prompt"),
        groupTitle: questionGroups.TrainingPreferences,
        answers: [
            {
                id: SportPreferAnswers.Morning,
                text: i18n.t("morning"),
            },
            {
                id: SportPreferAnswers.Afternoon,
                text: i18n.t("afternoon"),
            },
            {
                id: SportPreferAnswers.Evening,
                text: i18n.t("evening"),
            },
            {
                id: SportPreferAnswers.Different,
                text: i18n.t("different_times"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DESIRED_WORKOUT_TIME,
        event: `${prefix}_${QUESTIONS_IDS.DESIRED_WORKOUT_TIME}`,
        type: QuestionTypes.Radio,
        title: i18n.t("desired_workout_time_prompt"),
        groupTitle: questionGroups.TrainingPreferences,
        answers: [
            {
                id: DesiredWorkoutTimeAnswers.Thirty,
                text: i18n.t("30_min_or_less"),
            },
            {
                id: DesiredWorkoutTimeAnswers.FortyFive,
                text: i18n.t("45_min_or_less"),
            },
            {
                id: DesiredWorkoutTimeAnswers.Sixty,
                text: i18n.t("one_hour_or_so"),
            },
            {
                id: DesiredWorkoutTimeAnswers.SixtyPlus,
                text: i18n.t("over_an_hour"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.BODY_FAT,
        event: `${prefix}_${QUESTIONS_IDS.BODY_FAT}`,
        type: QuestionTypes.Range,
        groupTitle: questionGroups.PhysicalParameters,
        title: i18n.t("body_fat_level_prompt"),
        answers: Object.values(isFemale ? FemaleBodyFatRangeTypes : MaleBodyFatRangeTypes).map(
            (type) => ({
                id: type,
                value: type,
                img: isFemale
                    ? FemaleImagesByBodyFatRange[type as FemaleBodyFatRangeTypes]
                    : ImagesByBodyFatRange[type as MaleBodyFatRangeTypes],
            })
        ),
    },
    {
        id: QUESTIONS_IDS.HEIGHT,
        event: `${prefix}_${QUESTIONS_IDS.HEIGHT}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.HEIGHT,
        title: i18n.t("height_prompt"),
        groupTitle: questionGroups.PhysicalParameters,
    },
    {
        id: QUESTIONS_IDS.CURRENT_WEIGHT,
        event: `${prefix}_${QUESTIONS_IDS.CURRENT_WEIGHT}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.WEIGHT,
        title: i18n.t("current_weight_prompt"),
        groupTitle: questionGroups.PhysicalParameters,
    },
    {
        id: QUESTIONS_IDS.TARGET_WEIGHT,
        event: `${prefix}_${QUESTIONS_IDS.TARGET_WEIGHT}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.WEIGHT,
        weightType: "target",
        title: i18n.t("target_weight_prompt"),
        groupTitle: questionGroups.PhysicalParameters,
    },
    {
        id: QUESTIONS_IDS.AGE,
        event: `${prefix}_${QUESTIONS_IDS.AGE}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.AGE,
        title: i18n.t("age_prompt"),
        groupTitle: questionGroups.PhysicalParameters,
        progressBarBreakPoint: true,
    },
    {
        id: QUESTIONS_IDS.MOTIVATION,
        event: `${prefix}_${QUESTIONS_IDS.MOTIVATION}`,
        type: QuestionTypes.Radio,
        title: i18n.t("motivation_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: "necessary",
                text: i18n.t("necessary"),
                description: i18n.t("necessary_description"),
                svg: necessarySVG,
            },
            {
                id: "inconvenient",
                text: i18n.t("inconvenient"),
                description: i18n.t("inconvenient_description"),
                svg: inconvenientSVG,
            },
            {
                id: "enjoyable",
                text: i18n.t("enjoyable"),
                description: i18n.t("enjoyable_description"),
                svg: enjoyableSVG,
            },
        ],
    },
    ...(isFemale
        ? [
              {
                  id: QUESTIONS_IDS.ACTIVITY_LEVEL,
                  event: `${prefix}_${QUESTIONS_IDS.ACTIVITY_LEVEL}`,
                  type: QuestionTypes.Radio,
                  title: i18n.t("activity_level_prompt"),
                  groupTitle: questionGroups.Lifestyle,
                  answers: [
                      {
                          id: ActivityLevels.Sedentary,
                          text: i18n.t("sedentary"),
                          description: i18n.t("minimal_activity"),
                      },
                      {
                          id: ActivityLevels.ModerateExercise,
                          text: i18n.t("moderately_active", {
                              context: isFemale ? Genders.Female : Genders.Male,
                              defaultValue: i18n.t("moderately_active"),
                          }),
                          description: i18n.t("some_movement"),
                      },
                      {
                          id: ActivityLevels.Athlete,
                          text: i18n.t("very_active"),
                          description: i18n.t("physically_demanding"),
                      },
                  ],
              },
          ]
        : []),
    {
        id: QUESTIONS_IDS.EATING_HABITS,
        event: `${prefix}_${QUESTIONS_IDS.EATING_HABITS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("eating_habits_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: "emotional",
                text: i18n.t("emotional_eater"),
                description: i18n.t("emotional_eater_description"),
            },
            {
                id: "bored",
                text: i18n.t("bored_eater"),
                description: i18n.t("bored_eater_description"),
            },
            {
                id: "unconscious",
                text: i18n.t("unconscious_eater"),
                description: i18n.t("unconscious_eater_description"),
            },
            {
                id: "habitual",
                text: i18n.t("habitual_eater"),
                description: i18n.t("habitual_eater_description"),
            },
            {
                id: "energy",
                text: i18n.t("energy_eater"),
                description: i18n.t("energy_eater_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.COPE_STRESS,
        event: `${prefix}_${QUESTIONS_IDS.COPE_STRESS}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("cope_stress_prompt"),
        subtitle: i18n.t("cope_stress_motivation_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: "exercising",
                text: i18n.t("exercising"),
            },
            {
                id: "being_creative",
                text: i18n.t("being_creative"),
            },
            {
                id: "bad_habits",
                text: i18n.t("bad_habits"),
            },
            {
                id: "eating",
                text: i18n.t("eating"),
            },
            {
                id: "outdoors",
                text: i18n.t("outdoors"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SLEEP_AMOUNT,
        event: `${prefix}_${QUESTIONS_IDS.SLEEP_AMOUNT}`,
        type: QuestionTypes.Radio,
        title: i18n.t("sleep_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: SleepAmountAnswers.LessThan5,
                text: i18n.t("fewer_than_5_hours"),
            },
            {
                id: SleepAmountAnswers.From5To6,
                text: i18n.t("5_to_6_hours"),
            },
            {
                id: SleepAmountAnswers.From7To8,
                text: i18n.t("7_to_8_hours"),
            },
            {
                id: SleepAmountAnswers.Over8,
                text: i18n.t("over_8_hours"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DIET_TYPE,
        type: QuestionTypes.Radio,
        event: `${prefix}_dietary_preferences`,
        title: i18n.t("dietary_preferences_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: DietTypeAnswers.NoDiet,
                text: i18n.t("none"),
            },
            {
                id: DietTypeAnswers.Diabetes,
                text: i18n.t("diabetes"),
            },
            {
                id: DietTypeAnswers.Vegan,
                text: i18n.t("vegetarian_vegan"),
            },
            {
                id: DietTypeAnswers.GlutenFree,
                text: i18n.t("gluten_free"),
            },
            {
                id: DietTypeAnswers.LactoseFree,
                text: i18n.t("lactose_free"),
            },
            {
                id: DietTypeAnswers.NutAllergy,
                text: i18n.t("nut_allergy"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SUPPORT_IN_FITNESS,
        type: QuestionTypes.Radio,
        event: `${prefix}_${QUESTIONS_IDS.SUPPORT_IN_FITNESS}`,
        title: "Who supports you in your fitness journey?",
        groupTitle: questionGroups.MotivationAndSupport,
        answers: [
            {
                id: "family_members",
                text: "Family members",
            },
            {
                id: "friends",
                text: "Friends",
            },
            {
                id: "healthcare_professionals",
                text: "Healthcare professionals",
            },
            {
                id: "exercise_buddies",
                text: "Exercise buddies",
            },
            {
                id: "exercise_alone",
                text: "I prefer to exercise alone",
            },
            {
                id: "looking_for_support",
                text: "Looking for support",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.TRAINING_TRIGGER,
        event: `${prefix}_${QUESTIONS_IDS.TRAINING_TRIGGER}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("training_trigger_prompt"),
        subtitle: i18n.t("training_trigger_motivation_prompt"),
        groupTitle: questionGroups.MotivationAndSupport,
        answers: [
            {
                id: "job_requirements",
                text: i18n.t("job_requirements"),
                description: i18n.t("job_requirements_description"),
            },
            {
                id: "observing_inactivity",
                text: i18n.t("observing_inactivity"),
                description: i18n.t("observing_inactivity_description"),
            },
            {
                id: "visual_indicators",
                text: i18n.t("visual_indicators"),
                description: i18n.t("visual_indicators_description"),
            },
            {
                id: "parental_stamina",
                text: i18n.t("parental_stamina"),
                description: i18n.t("parental_stamina_description"),
            },
            {
                id: "clothing_fit",
                text: i18n.t("clothing_fit"),
                description: i18n.t("clothing_fit_description"),
            },
            {
                id: "health_improvement",
                text: i18n.t("health_improvement"),
                description: i18n.t("health_improvement_description"),
            },
            {
                id: "aging_concerns",
                text: i18n.t("aging_concerns"),
                description: i18n.t("aging_concerns_description"),
            },
            {
                id: "current_fitness_levels",
                text: i18n.t("current_fitness_levels"),
                description: i18n.t("current_fitness_levels_description"),
            },
            {
                id: "sexual_performance",
                text: i18n.t("sexual_performance"),
                description: i18n.t("sexual_performance_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.OBSTACLES,
        event: `${prefix}_${QUESTIONS_IDS.OBSTACLES}`,
        type: QuestionTypes.Checkbox,
        title: (
            <Trans
                i18nKey="obstacle_prompt"
                values={{
                    goal: seniorsGoalTextById[answers[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals],
                }}
            >
                <span className="text-white female:text-zing-warm-tangerine-500" />
            </Trans>
        ),
        groupTitle: questionGroups.MotivationAndSupport,
        answers: [
            {
                id: "consistencyLack",
                text: i18n.t("lack_of_consistency"),
            },
            {
                id: "timeConstraints",
                text: i18n.t("time_constraints"),
            },
            {
                id: "lackOfMotivation",
                text: i18n.t("lack_of_motivation"),
            },
            {
                id: "fatigue",
                text: i18n.t("fatigue"),
            },
            {
                id: "lackOfVisibleProgress",
                text: i18n.t("lack_of_visible_progress"),
            },
            {
                id: "plateausInProgress",
                text: i18n.t("plateaus"),
            },
            {
                id: "lackOfSupport",
                text: i18n.t("lack_of_support"),
            },
            {
                id: "injuriesOrPain",
                text: i18n.t("injuries_or_pain"),
            },
        ],
    },
    ...((answers?.[QUESTIONS_IDS.OBSTACLES] as string[])?.includes("consistencyLack")
        ? [
              {
                  id: QUESTIONS_IDS.PREVENT_EXERCISE_CONSISTENTLY,
                  event: `${prefix}_${QUESTIONS_IDS.PREVENT_EXERCISE_CONSISTENTLY}`,
                  type: QuestionTypes.Checkbox,
                  title: i18n.t("prevent_exercise_consistently_prompt"),
                  subtitle: i18n.t("prevent_exercise_consistently_motivation_prompt"),
                  groupTitle: questionGroups.MotivationAndSupport,
                  answers: [
                      {
                          id: "work_schedule",
                          text: i18n.t("work_schedule"),
                          description: i18n.t("work_schedule_description"),
                      },
                      {
                          id: "procrastination",
                          text: i18n.t("procrastination"),
                          description: i18n.t("procrastination_description"),
                      },
                      {
                          id: "unexpected_events",
                          text: i18n.t("unexpected_events"),
                          description: i18n.t("unexpected_events_description"),
                      },
                      {
                          id: "lack_of_accountability",
                          text: i18n.t("lack_of_accountability"),
                          description: i18n.t("lack_of_accountability_description"),
                      },
                  ],
              },
          ]
        : []),
    {
        id: QUESTIONS_IDS.ABILITY_TO_STAY_MOTIVATED,
        type: QuestionTypes.Radio,
        event: `${prefix}_${QUESTIONS_IDS.ABILITY_TO_STAY_MOTIVATED}`,
        title: i18n.t("ability_to_stay_motivated_prompt"),
        groupTitle: questionGroups.MotivationAndSupport,
        answers: [
            {
                id: "steadfast",
                text: i18n.t("steadfast"),
                description: i18n.t("steadfast_description"),
                svg: steadfastSVG,
            },
            {
                id: "resilient",
                text: i18n.t("resilient"),
                description: i18n.t("resilient_description"),
                svg: resilientSVG,
            },
            {
                id: "challenging",
                text: i18n.t("challenging"),
                description: i18n.t("challenging_description"),
                svg: challengingSVG,
            },
            {
                id: "fickle",
                text: i18n.t("fickle"),
                description: i18n.t("fickle_description"),
                svg: fickleSVG,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.MOTIVATE_MOST,
        event: `${prefix}_${QUESTIONS_IDS.MOTIVATE_MOST}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("motivate_most_prompt"),
        subtitle: i18n.t("motivate_most_motivation_prompt"),
        groupTitle: questionGroups.MotivationAndSupport,
        answers: [
            {
                id: "progress_tracking",
                text: i18n.t("progress_tracking"),
                description: i18n.t("progress_tracking_description"),
                svg: progressSVG,
            },
            {
                id: "feeling_better",
                text: i18n.t("feeling_better"),
                description: i18n.t("feeling_better_description"),
                svg: steadfastSVG,
            },
            {
                id: "achievements",
                text: i18n.t("achievements"),
                description: i18n.t("achievements_description"),
                svg: achievementsSVG,
            },
            {
                id: "discipline",
                text: i18n.t("discipline"),
                description: i18n.t("discipline_description"),
                svg: disciplineSVG,
            },
            {
                id: "music",
                text: i18n.t("music"),
                description: i18n.t("music_description"),
                svg: musicSVG,
            },
            {
                id: "challenges",
                text: i18n.t("challenges"),
                description: i18n.t("challenges_description"),
                svg: challengesSVG,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.ACHIEVE_FITNESS_GOAL,
        type: QuestionTypes.Radio,
        event: `${prefix}_${QUESTIONS_IDS.ACHIEVE_FITNESS_GOAL}`,
        title: i18n.t("achieve_fitness_goal_prompt"),
        groupTitle: questionGroups.MotivationAndSupport,
        answers: [
            {
                id: "highly_confident",
                text: i18n.t("highly_confident"),
                description: i18n.t("highly_confident_description"),
            },
            {
                id: "reasonably_confident",
                text: i18n.t("reasonably_confident"),
                description: i18n.t("reasonably_confident_description"),
            },
            {
                id: "optimistic",
                text: i18n.t("optimistic"),
                description: i18n.t("optimistic_description"),
            },
            {
                id: "not_confident",
                text: i18n.t("not_confident"),
                description: i18n.t("not_confident_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PROMO_HELP,
        event: `${prefix}_${QUESTIONS_IDS.PROMO_HELP}`,
        type: QuestionTypes.PromoHelp,
    },
];
